import { IonList, IonSpinner } from '@ionic/react'
import React, { useEffect } from 'react'
import ProcedureDynamicComponent from './ProcedureDynamic.component'
import ProcedureFooterComponent from './ProcedureFooter.component'
import ProcedureHeaderComponent from './ProcedureHeader.component'
import './Procedure.component.css'
import { useDispatch, useSelector } from 'react-redux'
import { getProcedureStateSelector } from './selectors/getProcedureState.selector'
import { IOption, IOptions, IPNNVOption } from '../../common/common.types'
import { ProcedureConstants } from './store/procedure.constants'
import { ITimesRowProps } from '../../features/times/timesRow/TimesRow.component'
import { getPatientTimelineSelector } from '../../features/timeline/selectors/patientTimeline.selector'

export interface IProcedureComponentProps extends IProcedureHeader, IProcedureFooter {
  type: string
  hideAttemptField?: boolean
  hideSuccessfulAttempt?: boolean
}

export interface IProcedureHeader {
  procedureTime: string
  procedureDate: string
  assessedBy: IOption | null
  successfulAttempt?: boolean
  attempts?: string | null
  pta: boolean
  rolePerforming: IOption | null
  rolePerformingPNNV?: IPNNVOption | null
  procedurePerformedByAnotherUnit: IOption | null
  procedurePerformedByAnotherUnitPNNV?: IPNNVOption | null
  isEkgImport?: number | null
}

export interface IProcedureFooter {
  procedureComplications?: IOptions
  response?: IOption | null
  responsePNNV?: IPNNVOption | null
  authorization?: IOption | null
  comments: string
}

const Procedure: React.FC<IProcedureComponentProps> = (props: IProcedureComponentProps) => {
  const dispatch = useDispatch()
  const {
    procedureTime,
    procedureDate,
    assessedBy,
    successfulAttempt,
    attempts,
    type,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    hideAttemptField = false,
    hideSuccessfulAttempt = false,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    pta,
    isEkgImport = null,
  } = props
  const state = useSelector(getProcedureStateSelector)
  const timelineState = useSelector(getPatientTimelineSelector)
  const { inputRows } = state
  const { procedureLoader } = timelineState

  useEffect(() => {
    procedureDate && dispatch({ type: `${type}/SET_DATE`, date: procedureDate })
    procedureTime && dispatch({ type: `${type}/SET_TIME`, time: procedureTime })
    inputRows?.forEach((row) =>
      row.inputs.forEach((input) => {
        if (input.type === ProcedureConstants.TIMES_ROW) {
          const timesProps = input.props as ITimesRowProps
          timesProps.time && dispatch({ type: timesProps.timeAction, time: timesProps.time })
          timesProps.date && dispatch({ type: timesProps.dateAction, date: timesProps.date })
        }
      })
    )
  }, [inputRows])

  return (
    <>
      {procedureLoader ? (
        <IonSpinner className='procedure-loader' data-testid='Spinner' />
      ) : (
        <IonList id='Procedures'>
          <ProcedureHeaderComponent
            isPHI
            procedureType={type}
            procedureTime={procedureTime}
            procedureDate={procedureDate}
            assessedBy={assessedBy}
            successfulAttempt={successfulAttempt}
            attempts={attempts}
            hideAttemptField={hideAttemptField}
            hideSuccessfulAttempt={hideSuccessfulAttempt}
            rolePerforming={rolePerforming}
            rolePerformingPNNV={rolePerformingPNNV}
            procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
            procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
            pta={pta}
            isEkgImport={isEkgImport}
          />
          {inputRows && <ProcedureDynamicComponent inputRows={inputRows} />}
          <ProcedureFooterComponent
            isPHI
            procedureType={type}
            procedureComplications={procedureComplications}
            response={response}
            responsePNNV={responsePNNV}
            authorization={authorization}
            comments={comments}
          />
        </IonList>
      )}
    </>
  )
}

export default Procedure
